import React, { useEffect, useState, useRef } from 'react';
import TitleWithContent from '@/components/elements/TitleWithContent/TitleWithContent';
import { motion } from 'framer-motion';
import { client } from '@/lib/sanity';

const cardItemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1.2,
      ease: 'easeOut',
    },
  },
};

type MediaItem = {
  id: string;
  type: 'image' | 'video';
  src: string;
  uploadedAt?: string;
  order?: number;
};

interface SanityGalleryItem {
  _id: string;
  title?: string;
  mediaFiles?: Array<{
    _type: 'image' | 'file';
    asset?: {
      _id: string;
      url: string;
      originalFilename?: string;
      mimeType?: string;
    };
  }>;
  order?: number;
  uploadedAt?: string;
}

const Gallery: React.FC = () => {
  const [mediaArray, setMediaArray] = useState<MediaItem[]>([]);
  const [showGallery, setShowGallery] = useState(false);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});
  const [soundEnabledIds, setSoundEnabledIds] = useState<Set<string>>(new Set());
  const [expandedVideoId, setExpandedVideoId] = useState<string | null>(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const sanityData: SanityGalleryItem[] = await client.fetch<SanityGalleryItem[]>(`*[_type == "galleryItem"] | order(order asc) {
          _id,
          title,
          mediaFiles[] {
            _type,
            asset->{
              _id,
              url,
              originalFilename,
              mimeType
            }
          },
          order,
          uploadedAt
        }`);

        const sanityItems: MediaItem[] = sanityData.flatMap((item): MediaItem[] => {
          const items: MediaItem[] = [];

          if (Array.isArray(item.mediaFiles) && item.mediaFiles.length > 0) {
            item.mediaFiles.forEach((file, index) => {
              const asset = file.asset;
              if (asset?.url) {
                const mime = asset.mimeType || '';
                const type = mime.startsWith('video/') ? 'video' : 'image';

                items.push({
                  id: `sanity-${item._id}-${index}`,
                  type,
                  src: asset.url,
                  order: item.order,
                  uploadedAt: item.uploadedAt,
                });
              }
            });
          }

          return items;
        });

        const res = await fetch(`/galleryimg/galleryimg.json?timestamp=${Date.now()}`, { cache: 'no-store' });
        console.log('Gallery fetch response status:', res.status);
        
        if (!res.ok) {
          throw new Error(`Failed to fetch gallery: ${res.status} ${res.statusText}`);
        }
        
        const localItemsRaw: MediaItem[] = await res.json();
        console.log('Gallery items loaded:', localItemsRaw);

        const localItems: MediaItem[] = localItemsRaw.map((item) => ({
          id: `local-${item.id}`,
          type: item.type,
          src: item.src,
        }));

        const sortedSanityItems = sanityItems.sort((a, b) => {
          if (a.uploadedAt && b.uploadedAt) {
            return new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime();
          }
          return 0;
        });

        const sortedLocalItems = localItems.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return 0;
        });

        const finalArray = [...sortedSanityItems, ...sortedLocalItems];
        console.log('Final media array:', finalArray);
        setMediaArray(finalArray);
      } catch (err) {
        console.error('❌ Error loading gallery:', err);
      }
    };

    fetchMedia();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowGallery(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleVideoClick = (id: string) => {
    const video = videoRefs.current[id];
    if (!video) return;

    if (expandedVideoId === id) {
      video.pause();
      video.muted = true;
      video.currentTime = 0;
      setSoundEnabledIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      setExpandedVideoId(null);
    } else {
      if (expandedVideoId !== null) {
        const prevVideo = videoRefs.current[expandedVideoId];
        if (prevVideo) {
          prevVideo.pause();
          prevVideo.muted = true;
          prevVideo.currentTime = 0;
          setSoundEnabledIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(expandedVideoId);
            return newSet;
          });
        }
      }
      video.muted = false;
      video.play();
      setSoundEnabledIds((prev) => new Set(prev).add(id));
      setExpandedVideoId(id);
    }
  };

  const handleMouseEnter = (id: string) => {
    if (expandedVideoId !== null) return;
    const video = videoRefs.current[id];
    if (video && !soundEnabledIds.has(id)) {
      video.muted = true;
      video.play();
    }
  };

  const handleMouseLeave = (id: string) => {
    if (expandedVideoId !== null) return;
    const video = videoRefs.current[id];
    if (video && !soundEnabledIds.has(id)) {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <div className="2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-3 my-[5rem]">
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.5 }}
      >
        <TitleWithContent title="Gallery" />
      </motion.div>

      {showGallery && (
        <>
          <div className={`w-full max-w-screen-2xl 2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-3 ${expandedVideoId !== null ? 'pointer-events-none select-none' : ''}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6 sm:px-2 md:px-8 lg:px-12 xl:px-16 2xl:px-20">
              {mediaArray.map((item) => (
                <motion.div
                  key={item.id}
                  variants={cardItemVariants}
                  initial="hidden"
                  animate="visible"
                  className="w-full overflow-hidden rounded-lg cursor-pointer relative"
                >
                  {item.type === 'image' ? (
                    <img
                      src={item.src}
                      alt="gallery"
                      className="w-full h-auto object-cover rounded-lg transition-transform duration-300 hover:scale-110"
                    />
                  ) : (
                    <>
                      <video
                        ref={(el) => (videoRefs.current[item.id] = el)}
                        src={item.src}
                        className="w-full max-h-80 object-cover rounded-lg"
                        onClick={() => handleVideoClick(item.id)}
                        onMouseEnter={() => handleMouseEnter(item.id)}
                        onMouseLeave={() => handleMouseLeave(item.id)}
                        muted
                        playsInline
                        preload="metadata"
                        controls={false}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-12 w-12 text-white"
                          viewBox="0 0 64 64"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="32" cy="32" r="30" fill="rgba(0,0,0,0.5)" />
                          <polygon points="26,20 46,32 26,44" fill="white" />
                        </svg>
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {expandedVideoId !== null && (
            <div
              onClick={() => handleVideoClick(expandedVideoId)}
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[1000] cursor-pointer"
            >
              <video
                ref={(el) => (videoRefs.current[expandedVideoId] = el)}
                src={mediaArray.find((m) => m.id === expandedVideoId)?.src}
                className="max-w-full max-h-full rounded-lg"
                autoPlay
                controls
                muted={false}
                playsInline
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Gallery;
